/**
 * Workflow API Service — Architecture 3.1 (Hard Split)
 *
 * 提供前端与 `/api/workflows/*` 端点的通信桥梁。
 * 设计原则：100% 原生 fetch，与 api.ts 风格一致。
 */

import { API_BASE_URL } from './api';

// 复用 api.ts 的鉴权辅助
function getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
}

function createAuthHeaders(): Record<string, string> {
    const token = getAuthToken();
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
}

// ─── 类型定义 ─────────────────────────────

export interface MarketplaceAgent {
    id: string;
    name: string;
    description: string;
    persona_prompt?: string;
    guidance_prompt?: string; // 向后兼容
    pricing_model: 'free' | 'pay_per_use' | 'subscription';
    price: number;
    version: string;
    creator_id: string;
    node_count: number;
    security_level?: string;
    taxonomy?: Record<string, unknown>;
    created_at: string;
    updated_at: string;
}

/** @deprecated 使用 MarketplaceAgent */
export type MarketplaceSkill = MarketplaceAgent;

export interface InstalledAgent {
    id: string;
    name: string;
    description: string;
    persona_prompt?: string;
    pricing_model: 'free' | 'pay_per_use' | 'subscription';
    price: number;
    is_published: boolean;
    security_level?: string;
    equipped_skills?: string[];
    source: 'owned' | 'installed';
}

/** @deprecated 使用 InstalledAgent */
export type InstalledSkill = InstalledAgent;

export interface SkillItem {
    id: string;
    name: string;
    display_name: string;
    description?: string;
    category?: string;
    is_builtin: boolean;
}

/** 装备项类型（与后端 EquippedItem 对齐） */
export interface EquippedItem {
    type: 'tool' | 'skill' | 'agent';
    id: string;
}

export interface CreateAgentRequest {
    name: string;
    description?: string;
    persona_prompt: string;
    equipped_skills: EquippedItem[];
    security_level: 'standard' | 'hitl_required';
    visibility?: 'private' | 'public';
}

// ─── API 方法 ─────────────────────────────

export class WorkflowApiService {

    /**
     * 专家大厅：获取所有已发布的专家
     */
    static async getMarketplace(): Promise<{ items: MarketplaceAgent[] }> {
        const response = await fetch(`${API_BASE_URL}/api/workflows/marketplace`, {
            headers: createAuthHeaders(),
        });
        if (!response.ok) {
            throw new Error(`Marketplace fetch failed: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * 获取所有可用的工具列表（供 ExpertSetupDesk 使用）
     */
    static async getSkills(): Promise<{ items: SkillItem[] }> {
        const response = await fetch(`${API_BASE_URL}/api/workflows/skills`, {
            headers: createAuthHeaders(),
        });
        if (!response.ok) {
            throw new Error(`Skills fetch failed: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * 获取当前用户已安装的专家列表
     */
    static async getInstalled(): Promise<{ items: InstalledAgent[] }> {
        const response = await fetch(`${API_BASE_URL}/api/workflows/installed`, {
            headers: createAuthHeaders(),
        });
        if (!response.ok) {
            throw new Error(`Installed agents fetch failed: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * 创建新专家（由 ExpertSetupDesk 调用）
     */
    static async createAgent(data: CreateAgentRequest): Promise<{
        success: boolean;
        agent: { id: string; name: string; created_at: string };
    }> {
        const response = await fetch(`${API_BASE_URL}/api/workflows/create-agent`, {
            method: 'POST',
            headers: createAuthHeaders(),
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            throw new Error(`Create agent failed: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * 安装/订阅一个专家
     */
    static async install(agentId: string): Promise<{
        success: boolean;
        message: string;
        pricingModel: string;
        price: number;
    }> {
        const response = await fetch(`${API_BASE_URL}/api/workflows/install`, {
            method: 'POST',
            headers: createAuthHeaders(),
            body: JSON.stringify({ agentId }),
        });
        if (!response.ok) {
            throw new Error(`Install failed: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * 卸载/退订一个已安装的专家
     */
    static async uninstall(agentId: string): Promise<{
        success: boolean;
        affected: number;
    }> {
        const response = await fetch(`${API_BASE_URL}/api/workflows/install/${agentId}`, {
            method: 'DELETE',
            headers: createAuthHeaders(),
        });
        if (!response.ok) {
            throw new Error(`Uninstall failed: ${response.status}`);
        }
        return await response.json();
    }
}
